import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";

import { formatAccount, normalizeCurrencyCode } from "../../../features/ValueFormater";
import { roleAuth } from "../../../utils/functionAuthorization";

import PageHeader from "../../Components/Common/PageHeader";
import LegalStatementHistory from "../../Components/StatementOfAccount/LegalStatementHistory";
import MerchantSalesHistory from "../../Components/StatementOfAccount/MerchantSalesHistory";

import useNewBslAccountAPI from "../../api/bslAccountAPI";
import { findBslOverviewAccount, getBslAccountNumber, getBslIban } from "../../utils/bslAccountMatching";

import { setFeatureData } from "../../store/slices/appSlice";

import "../../Styles/pages/statement-of-account.scss";

const ACCESS_POINTS = {
  transactionsLBP:
    "VIEW_TRANSACTIONS_OF_LBP_ACCOUNT",
  transactionsUSD:
    "VIEW_TRANSACTIONS_OF_USD_ACCOUNT",
  transactionsEUR:
    "VIEW_TRANSACTIONS_OF_EUR_ACCOUNT",
};
const EMPTY_ACCOUNTS = Object.freeze([]);

export default function StatementOfAccountPage({
  onOpenQrAfterGenerate,
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const entityType =
    sessionStorage.getItem("EntityType") || "";

  const isLegalEntity =
    entityType === "LEGALENTITY";

  const entityMobile =
    sessionStorage.getItem("EntityMobile") || "";

  const profilePicture =
    sessionStorage.getItem("UserIconUrl") || "";

  const {
    GetBankAccountBalance,
    GetBankAccountOverview,
  } = useNewBslAccountAPI();

  const bankOverview = useSelector(
    (state) =>
      state?.app?.tables?.bslAccountOverview
        ?.data || EMPTY_ACCOUNTS,
  );
  const bankBalanceAccounts = useSelector(
    (state) => state?.app?.summary?.bslCurrenciesOverview || EMPTY_ACCOUNTS,
  );

  const selectedStatementAccount =
    useSelector(
      (state) =>
        state?.app?.details
          ?.selectedStatementAccount || null,
    );

  /*
   * ========================================
   * LEGAL ENTITY - OLD FUNCTIONALITY
   * ========================================
   */

  const storedLegalAccounts = useMemo(() => {
    try {
      const parsedAccounts = JSON.parse(
        sessionStorage.getItem("AccountType") ||
          "[]",
      );

      return Array.isArray(parsedAccounts)
        ? parsedAccounts
        : [];
    } catch (error) {
      console.error(
        "Error parsing AccountType:",
        error,
      );

      return [];
    }
  }, []);

  const [legalBalances, setLegalBalances] =
    useState({
      USD: 0,
      LBP: 0,
      details: {},
    });

  const [
    legalBslBalances,
    setLegalBslBalances,
  ] = useState({
    USD: 0,
    LBP: 0,
    details: {},
  });

  const [
    legalAccountDropdownOpen,
    setLegalAccountDropdownOpen,
  ] = useState(false);

  const [phoneCopied, setPhoneCopied] =
    useState(false);

  const legalAccounts = useMemo(() => {
    if (!isLegalEntity) {
      return [];
    }

    const mappedAccounts =
      storedLegalAccounts.filter(
        (account) => Number(account.AccountType) === 1,
      ).map(
        (account, index) => {
          const currencyId = Number(
            account.CurrencyID,
          );

          const accountType = Number(
            account.AccountType,
          );

          const selectionKey =
            `${currencyId}-${accountType}-${index}`;

          return {
            label: account.Name,
            value: normalizeCurrencyCode(currencyId),
            currencyId,
            accountType,
            selectionKey,
            rawAccount: account,
          };
        },
      );

    const bankAccounts = bankBalanceAccounts.length > 0
      ? bankBalanceAccounts
      : bankOverview;
    bankAccounts.forEach((account, index) => {
      const currencyId = Number(account.CurrencyID);
      const accountNumber = getBslAccountNumber(account);
      if (!Number.isFinite(currencyId) || currencyId <= 0 || !accountNumber) return;
      const overviewAccount = findBslOverviewAccount(bankOverview, accountNumber);

      const currency = normalizeCurrencyCode(currencyId);
      mappedAccounts.push({
        label: `BSL ${currency} Account`,
        accountNumber,
        value: currency,
        currencyId,
        accountType: 2,
        selectionKey: `${currencyId}-2-bank-${index}`,
        rawAccount: {
          ...overviewAccount,
          ...account,
        },
      });
    });

    const canViewUSD = roleAuth(
      "statementofaccount",
      ACCESS_POINTS.transactionsUSD,
    );

    const canViewLBP = roleAuth(
      "statementofaccount",
      ACCESS_POINTS.transactionsLBP,
    );
    const canViewEUR = roleAuth(
      "statementofaccount",
      ACCESS_POINTS.transactionsEUR,
    );

    return mappedAccounts.filter(
      (account) => {
        if (
          account.currencyId === 1 &&
          canViewUSD
        ) {
          return true;
        }

        if (
          account.currencyId === 2 &&
          canViewLBP
        ) {
          return true;
        }

        return account.accountType === 2 && account.currencyId === 178 && canViewEUR;
      },
    );
  }, [
    isLegalEntity,
    storedLegalAccounts,
    bankOverview,
    bankBalanceAccounts,
  ]);

  useEffect(() => {
    if (!isLegalEntity) {
      return;
    }

    GetBankAccountOverview();
  }, [isLegalEntity]);

  useEffect(() => {
    if (!isLegalEntity) {
      return;
    }

    let cancelled = false;

    const fetchBankBalance = async () => {
      try {
        const today = new Date();

        const dateTo = today
          .toISOString()
          .split("T")[0];

        const dateFrom = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 1,
        )
          .toISOString()
          .split("T")[0];

        const response =
          await GetBankAccountBalance(
            dateFrom,
            dateTo,
          );

        if (cancelled) {
          return;
        }

        const currenciesOverview =
          response?.Payload
            ?.CurrenciesOverview || [];

        const newBslBalances = {
          USD: 0,
          LBP: 0,
          EUR: 0,
          details: {},
        };

        currenciesOverview.forEach(
          (currency) => {
            const currencyId = Number(
              currency.CurrencyID,
            );

            const currencyName = normalizeCurrencyCode(currencyId);

            const currencyBalances =
              currency?.CurrencyBalances || {};

            const available = Number(
              currencyBalances.Available ?? 0,
            );

            const book = Number(
              currencyBalances.Book ?? 0,
            );

            const held = Number(
              currencyBalances.Held ?? 0,
            );

            newBslBalances[currencyName] =
              book;

            newBslBalances.details[
              currencyName
            ] = {
              available,
              book,
              held,
            };
          },
        );

        setLegalBslBalances(
          newBslBalances,
        );
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Failed to get BSL account balance:",
            error,
          );

          setLegalBslBalances({
            USD: 0,
            LBP: 0,
            details: {},
          });
        }
      }
    };

    fetchBankBalance();

    return () => {
      cancelled = true;
    };
  }, [isLegalEntity]);

  const legalSelectedAccountData =
    useMemo(() => {
      const requestedKey = searchParams.get("accountKey");
      const requestedAccount = requestedKey
        ? legalAccounts.find((account) => account.selectionKey === requestedKey)
        : null;
      const clickedAccountNumber = String(location.state?.accountNumber ?? "").trim();
      const clickedAccount = clickedAccountNumber
        ? legalAccounts.find((account) =>
            account.accountType === 2 && account.accountNumber === clickedAccountNumber,
          )
        : null;
      const storedAccount = legalAccounts.find(
        (account) => account.selectionKey === selectedStatementAccount?.selectionKey,
      );
      const findAccount = (accountType, currencyId) =>
        legalAccounts.find(
          (account) =>
            account.accountType === Number(accountType) &&
            account.currencyId === Number(currencyId),
        );

      return requestedAccount || clickedAccount || findAccount(
        searchParams.get("accountType"),
        searchParams.get("currencyId"),
      ) || storedAccount || findAccount(
        selectedStatementAccount?.accountType,
        selectedStatementAccount?.currencyId,
      ) || legalAccounts[0] || null;
    }, [
      legalAccounts,
      searchParams,
      location.state?.accountNumber,
      selectedStatementAccount?.accountType,
      selectedStatementAccount?.currencyId,
      selectedStatementAccount?.selectionKey,
    ]);

  const legalSelectedAccount = legalSelectedAccountData?.value || "";
  const legalSelectedAccountKey = legalSelectedAccountData?.selectionKey || "";
  const legalAccountSelected = legalSelectedAccountData?.accountType ?? null;

  useEffect(() => {
    if (!isLegalEntity || !legalSelectedAccountData) return;
    const { accountType, currencyId, selectionKey, accountNumber } = legalSelectedAccountData;
    if (
      Number(selectedStatementAccount?.accountType) === accountType &&
      Number(selectedStatementAccount?.currencyId) === currencyId &&
      selectedStatementAccount?.selectionKey === selectionKey &&
      selectedStatementAccount?.accountNumber === accountNumber
    ) return;

    dispatch(setFeatureData({
      section: "details",
      key: "selectedStatementAccount",
      value: { accountType, currencyId, selectionKey, accountNumber },
    }));
  }, [isLegalEntity, legalSelectedAccountData, selectedStatementAccount, dispatch]);

  const availableLegalAccounts =
    legalAccounts.filter(
      (account) =>
        account.selectionKey !==
        legalSelectedAccountKey,
    );

  const handleLegalAccountSelect = (
    account,
  ) => {
    if (!account) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("accountType", String(account.accountType));
    nextParams.set("currencyId", String(account.currencyId));
    nextParams.set("accountKey", account.selectionKey);
    setSearchParams(nextParams, { replace: true });

    dispatch(
      setFeatureData({
        section: "details",
        key: "selectedStatementAccount",
        value: {
          accountType: Number(
            account.accountType,
          ),
          currencyId: Number(
            account.currencyId,
          ),
          selectionKey: account.selectionKey,
          accountNumber: account.accountNumber,
        },
      }),
    );

    setLegalAccountDropdownOpen(false);
  };

  const legalCurrentBalances =
    Number(legalAccountSelected) === 2
      ? legalBslBalances
      : legalBalances;

  const legalSelectedBalanceDetails =
    legalSelectedAccountData?.rawAccount?.CurrencyBalances
      ? {
          available: legalSelectedAccountData.rawAccount.CurrencyBalances.Available ?? 0,
          book: legalSelectedAccountData.rawAccount.CurrencyBalances.Book ?? 0,
          held: legalSelectedAccountData.rawAccount.CurrencyBalances.Held ?? 0,
        }
      : legalCurrentBalances?.details?.[
      legalSelectedAccount
    ] || {};

  const legalCurrencyId =
    Number(
      legalSelectedAccountData
        ?.currencyId,
    ) || 1;

  const legalIbanNumber = legalSelectedAccountData?.accountType === 2
    ? getBslIban(bankOverview, legalSelectedAccountData?.accountNumber)
    : "";

  const handleCopyPhone = async () => {
    try {
      const textToCopy =
        Number(legalAccountSelected) === 1
          ? entityMobile
          : legalIbanNumber;

      await navigator.clipboard.writeText(
        textToCopy,
      );

      setPhoneCopied(true);

      setTimeout(() => {
        setPhoneCopied(false);
      }, 500);
    } catch (error) {
      console.error(
        "Failed to copy:",
        error,
      );
    }
  };

  /*
   * ========================================
   * NON-LEGAL / MERCHANT - NEW FUNCTIONALITY
   * ========================================
   */

  const [
    merchantBalances,
    setMerchantBalances,
  ] = useState({
    USD: 0,
    LBP: 0,
  });

  const merchantAccounts = useMemo(() => {
    if (isLegalEntity) {
      return [];
    }

    return [
      {
        label: "SUYOOL USD Account",
        value: "USD",
      },
      {
        label: "SUYOOL LBP Account",
        value: "LBP",
      },
    ];
  }, [isLegalEntity]);

  const [
    merchantSelectedAccount,
    setMerchantSelectedAccount,
  ] = useState("USD");

  useEffect(() => {
    if (isLegalEntity) {
      return;
    }

    if (
      !merchantAccounts.some(
        (account) =>
          account.value ===
          merchantSelectedAccount,
      )
    ) {
      setMerchantSelectedAccount(
        merchantAccounts[0]?.value ||
          "USD",
      );
    }
  }, [
    isLegalEntity,
    merchantAccounts,
    merchantSelectedAccount,
  ]);

  const merchantCurrencyId =
    merchantSelectedAccount === "USD"
      ? 1
      : 2;

  const merchantSelectedBalance =
    merchantBalances?.[
      merchantSelectedAccount
    ] ?? 0;

  return (
    <section className="new-bank-transaction-history">
      <PageHeader
        className="new-bank-transaction-history__heading"
        icon="/Images/trx-history-icon.svg"
        title="Transaction History"
        subtitle={
          isLegalEntity
            ? "View and manage all your transactions"
            : "Review sales activity, filter by branch or till, and export transaction history."
        }
      />

      {isLegalEntity ? (
        <div className="new-bank-transaction-history__topbar">
          <div className="new-bank-transaction-history__balance-block">
            <label className="new-bank-transaction-history__account-switch">
              <div
                className={`select__logo__header ${
                  legalAccountDropdownOpen
                    ? "select__logo__header--open"
                    : ""
                }`}
              >
                <div className="custom-account-select">
                  <button
                    type="button"
                    className={`custom-account-select__trigger ${
                      legalAccountDropdownOpen
                        ? "custom-account-select__trigger--open"
                        : ""
                    }`}
                    onClick={() =>
                      setLegalAccountDropdownOpen(
                        (previous) =>
                          !previous,
                      )
                    }
                  >
                    <span className="custom-account-select__trigger-content">
                      <span className="custom-account-select__trigger-logo">
                        {Number(legalSelectedAccountData?.accountType) === 1 ? (
                          <img
                            src="/Images/SuyoolAccountLogo.png"
                            alt="Suyool"
                          />
                        ) : (
                          <img
                            src="https://suyoolportalpublic.blob.core.windows.net/photos/bslImagesDas/BSL-logo.svg"
                            alt="BSL"
                          />
                        )}
                      </span>

                      <span className="custom-account-select__label">
                        {legalSelectedAccountData
                          ?.label ||
                          "No Account"}
                      </span>
                    </span>

                    <span
                      className={`custom-account-select__arrow ${
                        legalAccountDropdownOpen
                          ? "custom-account-select__arrow--open"
                          : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`custom-account-select__dropdown ${
                      legalAccountDropdownOpen
                        ? "custom-account-select__dropdown--open"
                        : ""
                    }`}
                  >
                    <div className="custom-account-select__all-accounts">
                      All Accounts
                    </div>

                    <hr className="custom-account-select__divider" />

                    <div className="custom-account-select__options">
                      {availableLegalAccounts.map(
                        (account) => {
                          const accountBalances =
                            Number(
                              account.accountType,
                            ) === 2
                              ? legalBslBalances
                              : legalBalances;

                          const accountBook =
                            account.rawAccount?.CurrencyBalances?.Book ?? accountBalances
                              ?.details?.[
                              account.value
                            ]?.book ?? 0;

                          return (
                            <button
                              type="button"
                              key={
                                account.selectionKey
                              }
                              className="custom-account-select__option"
                              onClick={() =>
                                handleLegalAccountSelect(
                                  account,
                                )
                              }
                            >
                              <span className="custom-account-select__option-logo">
                                <img
                                  src={
                                    Number(
                                      account.accountType,
                                    ) === 1
                                      ? "/Images/SuyoolAccountLogo.png"
                                      : "https://suyoolportalpublic.blob.core.windows.net/photos/bslImagesDas/BSL-logo.svg"
                                  }
                                  alt={
                                    Number(
                                      account.accountType,
                                    ) === 1
                                      ? "Suyool"
                                      : "BSL"
                                  }
                                />
                              </span>

                              <span className="custom-account-select__option-label">
                                {
                                  account.label
                                }
                              </span>

                              <span className="custom-account-select__option-book">
                                {formatAccount(
                                  accountBook,
                                  account.currencyId,
                                )}
                              </span>

                              <hr className="custom-account-select__divider" />
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </label>

            <div
              style={{
                marginTop: "10px",
              }}
            >
              <strong className="strong__transaction">
                {formatAccount(
                  legalSelectedBalanceDetails
                    .book ?? 0,
                  legalCurrencyId,
                )}
              </strong>
            </div>

            <hr className="hr__transaction__account" />

            <div className="new-bank-transaction-history__balance-details">
              <div className="new-bank-transaction-history__balance-item">
                <span className="new-bank-transaction-history__balance-label">
                  Available
                </span>

                <span className="new-bank-transaction-history__balance-value">
                  {formatAccount(
                    legalSelectedBalanceDetails
                      .available ?? 0,
                    legalCurrencyId,
                  )}
                </span>
              </div>

              <span
                className="new-bank-transaction-history__balance-separator"
                aria-hidden="true"
              />

              <div className="new-bank-transaction-history__balance-item">
                <span className="new-bank-transaction-history__balance-label">
                  Book
                </span>

                <span className="new-bank-transaction-history__balance-value">
                  {formatAccount(
                    legalSelectedBalanceDetails
                      .book ?? 0,
                    legalCurrencyId,
                  )}
                </span>
              </div>

              <span
                className="new-bank-transaction-history__balance-separator"
                aria-hidden="true"
              />

              <div className="new-bank-transaction-history__balance-item">
                <span className="new-bank-transaction-history__balance-label">
                  Hold
                </span>

                <span className="new-bank-transaction-history__balance-value">
                  {formatAccount(
                    legalSelectedBalanceDetails
                      .held ?? 0,
                    legalCurrencyId,
                  )}
                </span>
              </div>

              <div className="phone_transaction_head">
                <span className="phone_transaction_head__span">
                  {Number(legalSelectedAccountData?.accountType) === 1 ? (
                    profilePicture ? (
                      <img
                        src={profilePicture}
                        alt="User Profile"
                        className="user__profile__image"
                      />
                    ) : (
                      <div className="user__logo__div">
                        <img
                          src="/Images/UserLogo.png"
                          alt="User"
                          className="user__default__image"
                        />
                      </div>
                    )
                  ) : (
                    <div className="user__iban__div">
                      <img
                        src="/Images/IbanLogo.png"
                        alt="IBAN"
                        className="user__iban__image"
                      />
                    </div>
                  )}

                  <div>
                    <div>
                      <span className="phone__transaction">
                        {Number(legalSelectedAccountData?.accountType) === 1
                          ? "Phone Number"
                          : "IBAN"}
                      </span>
                    </div>

                    <div>
                      {Number(legalSelectedAccountData?.accountType) === 1
                        ? `+${entityMobile}`
                        : legalIbanNumber}
                    </div>
                  </div>

                  <img
                    src={
                      phoneCopied
                        ? "/Images/copy.svg"
                        : "/Images/CheckIcon.png"
                    }
                    alt={
                      phoneCopied
                        ? "Copied"
                        : "Copy"
                    }
                    onClick={
                      handleCopyPhone
                    }
                    style={{
                      cursor: "pointer",
                      marginLeft: "20px",
                      marginTop: "20px",
                    }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="new-merchant-transaction">
          <div className="new-merchant-transaction__block">
            <strong className="strong_text_merchant">
              {formatAccount(
                merchantSelectedBalance,
                merchantCurrencyId,
              )}
            </strong>
</div>
            <label className="new-merchant-transaction-history__account-switch">
              <span className="span_text_merchant">
                Transactions on
              </span>

              <select className="select_merchant_history"
                value={
                  merchantSelectedAccount
                }
                onChange={(event) =>
                  setMerchantSelectedAccount(
                    event.target.value,
                  )
                }
              >
                {merchantAccounts.map(
                  (account) => (
                    <option
                      key={account.value}
                      value={account.value}
                    >
                      {account.label}
                    </option>
                  ),
                )}
              </select>
            </label>
          
        </div>
      )}

      <hr className="color-gray" />

      {isLegalEntity ? (
        <LegalStatementHistory
          key={`legal-history-${legalSelectedAccountKey}`}
          currencyId={
            legalCurrencyId
          }
          selectedAccount={
            legalSelectedAccount
          }
          accountSelected={
            legalAccountSelected
          }
          onBalancesChange={
            setLegalBalances
          }
          onOpenQrAfterGenerate={
            onOpenQrAfterGenerate
          }
        />
      ) : (
        <MerchantSalesHistory
          currencyId={
            merchantCurrencyId
          }
          onBalancesChange={
            setMerchantBalances
          }
          onOpenQrAfterGenerate={
            onOpenQrAfterGenerate
          }
        />
      )}
    </section>
  );
}
